import React, { lazy } from "react";
import { Navigate, Route, useLocation, useSearchParams } from "react-router-dom";
import { lazyWithRetry, lazyWithRetryNamed } from "@/lib/lazyWithRetry";

import { OptionalAuth, RequireAuth, RequireGuest } from "@/components/auth/AuthGate";
import UniversalAuthCallback from "@/components/auth/UniversalAuthCallback";
import { RouteGuard } from "@/components/runtime/route-guard/RouteGuard";
import AddAddressPage from "@/pages/AddAddressPage";
import { AddressPage } from "@/pages/Address";
import AdminAccessCheck from "@/pages/AdminAccessCheck";
import AdminAccessDenied from "@/pages/AdminAccessDenied";
import AdminGateDebug from "@/pages/AdminGateDebug";
import Auth from "@/pages/Auth";
import BridgeCustomer from "@/pages/BridgeCustomer";
import Coverage from "@/pages/Coverage";
import CreateAddressPage from "@/pages/CreateAddressPage";
import Dashboard from "@/pages/Dashboard";
import EnterprisePage from "@/pages/EnterprisePage";
import ForBusiness from "@/pages/ForBusiness";
import ForCryptoBusinesses from "@/pages/ForCryptoBusinesses";
import ForFinanceTeams from "@/pages/ForFinanceTeams";
import ForFreelancers from "@/pages/ForFreelancers";
import ForGlobalEarners from "@/pages/ForGlobalEarners";
import HowItWorks from "@/pages/HowItWorks";
import HowPaymentsWork from "@/pages/HowPaymentsWork";
import KycFlowPage from "@/pages/KycFlowPage";
import Landing from "@/pages/Landing";
import NotFound from "@/pages/NotFound";
import OnboardingProfile from "@/pages/OnboardingProfile";
import { ProductsOverview } from "@/pages/ProductsOverview";
import ResumeVerificationPage from "@/pages/ResumeVerificationPage";
import SupportCenter from "@/pages/SupportCenter";
import SupportTicketDetail from "@/pages/SupportTicketDetail";
import SupportTickets from "@/pages/SupportTickets";
import ToSWaitingPage from "@/pages/ToSWaitingPage";
import { TransactionHistoryPage } from "@/pages/TransactionHistoryPage";
import UserAccessDenied from "@/pages/UserAccessDenied";
import Verify from "@/pages/Verify";
import WalletPageRoute from "@/pages/WalletPage";
import WithdrawPage from "@/pages/WithdrawPage";
import { DashboardIndex } from "@/pages/dashboard/DashboardIndex";
import {
  DashboardCalculator,
  DashboardHelp,
  DashboardSettings,
  DashboardTelegramRoute,
  SendPageRoute,
  TransactionDetailRoute,
  VirtualAccountDetailRoute,
  WalletDetailRoute,
  WalletOverview
} from "@/pages/dashboard/DashboardRoutes";
import LiquidationAddressPage from "@/pages/products/LiquidationAddressPage";
import PayoutRoutesPage from "@/pages/products/PayoutRoutesPage";
import VirtualAccountPage from "@/pages/products/VirtualAccountPage";
import WalletPage from "@/pages/products/WalletPage";

const lazyNamedExport = <T extends React.ComponentType<any>>(factory: () => Promise<Record<string, unknown>>, exportName: string, displayName: string) =>
  lazy(async () => {
    const mod = await factory();
    const component = mod[exportName] as T | undefined;
    if (!component) {
      if (import.meta?.env?.DEV) {
        console.error(`[routeTree] Missing lazy export`, { exportName: displayName });
      }
      return { default: () => null };
    }
    return { default: component };
  });

// Bundle Optimization: Lazy load Telegram pages (not all users use Telegram, ~5-7KB saved)
const TgAuth = lazy(() => import("@/app/pages/telegram/TgAuth"));
// REMOVED: Duplicate Telegram UI components (Single UI architecture)
// TgDashboard, TgKyc, TgDeposit, TgSend, TgDashboardGuard
// Now using main /dashboard with Telegram-aware features
const TelegramShell = lazy(() => import("@/app/shell/TelegramShell"));
// Phase 9: Progressive Onboarding Wizard (lazy loaded)
const OnboardingWizard = lazyNamedExport(() => import("@/components/onboarding"), "OnboardingWizard", "OnboardingWizard");
// Phase 5.2: Lazy load heavy/rarely-used pages (reduce bundle ~15-20KB)
const Analytics = lazy(() => import("@/pages/Analytics"));
const CheckoutNew = lazy(() => import("@/pages/checkout/New"));
const CheckoutShow = lazy(() => import("@/pages/checkout/Show"));
const P3BridgeDemo = lazy(() => import("@/pages/P3BridgeDemo"));
const Hero3DDemo = lazy(() => import("@/pages/Hero3DDemo"));
const UserWalletDemo = lazy(() => import("@/pages/UserWalletDemo"));
const UserSettingsPage = lazyNamedExport(() => import("@/pages/UserSettingsPage"), "UserSettingsPage", "UserSettingsPage");
const WaitlistPage = lazy(() => import("@/pages/Waitlist"));
const WaitlistConfirmPage = lazy(() => import("@/pages/WaitlistConfirm"));
const UiKitShowcase = lazy(() => import("@/pages/UiKitShowcase"));
const InputDemo = lazy(() => import("@/pages/InputDemo"));
// Direct lazy import for FeePage to avoid Safari iOS module loading issues
const FeePage = lazy(() =>
  import("@/pages/FeePage").then((module) => ({
    default: module.FeePage,
  }))
);
const CanonPlayground = lazy(() => import("@/pages/CanonPlayground"));
const BrandAssetsPage = lazy(() => import("@/pages/BrandAssetsPage"));
const BrandPlatformsPage = lazy(() => import("@/pages/BrandPlatformsPage"));
const SupaFABShowcase = lazy(() => import("@/pages/design/SupaFABShowcase"));

// Keep frequently-used pages eager
// Bundle Optimization: Lazy load legal pages (rarely visited, ~3-5KB saved)
const Privacy = lazy(() => import("@/app/pages/legal/Privacy"));
const Terms = lazy(() => import("@/app/pages/legal/Terms"));

// CRITICAL: Admin layout uses lazyWithRetry to handle chunk loading failures
// This prevents "Importing a module script failed" errors (Issue #621)
const AdminUnifiedLayout = lazyWithRetry(() =>
  import("@/components/admin/AdminUnifiedLayout").then((module) => ({
    default: module.default
  }))
);

const createGuardedRoute = (
  _path: string,
  vnextComponent: React.ComponentType | null,
  fallbackComponent: React.ComponentType
) => vnextComponent || fallbackComponent;

// ────────────────────────────────────────────────────────────────────────────
// Variant B (systemic): any *VNext* admin screen is auto-wrapped in the unified
// admin layout (LegacyAdminWrapper). Even if someone later sets a standalone
// component, LeftSidebar will persist. Security-by-default ✅
// ────────────────────────────────────────────────────────────────────────────
const withAdminLayout = (C: React.ComponentType<any>) => (props: any) => (
  <LegacyAdminWrapper>
    <C {...props} />
  </LegacyAdminWrapper>
);

const AddressPageVNext = null;
// may be later changed to NewAdminDashboard; the wrapper will keep layout intact
const AdminDashboardVNext: React.ComponentType<any> | null = null;
const AdminDashboardVNextWrapped =
  AdminDashboardVNext ? withAdminLayout(AdminDashboardVNext) : null;
const AuthPageVNext = null;
const WalletPageVNext = null;
const HistoryPageVNext = null;
const SettingsPageVNext = null;
const AdminBridgeVNext = null;

const LegacyAdminWrapper = () => {
  const location = useLocation();

  // 🛡️ CRITICAL FIX: Handle legacy URLs with query parameters
  // URL like /admin?tab=transfers#settings → Redirect to /admin#transfers
  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    // E2E Testing Bypass
    if (window.localStorage.getItem('e2e_admin_bypass') === 'true') {
      console.log('[E2E Bypass] LegacyAdminWrapper rendering with admin bypass active');
    }

    // Check for legacy query parameters (?tab=something)
    const queryParams = new URLSearchParams(location.search || '');
    const tabParam = queryParams.get('tab');

    // Only redirect legacy URLs on the root /admin route.
    // Nested admin routes (e.g. /admin/users/:id?tab=compliance) legitimately use the same search param
    // to control in-page tabs. Redirecting there breaks deep links by bouncing back to the dashboard.
    const isRootAdminRoute = location.pathname === '/admin' || location.pathname === '/admin/';

    if (tabParam && isRootAdminRoute) {
      console.warn('[LegacyAdminWrapper] Legacy URL detected with ?tab= parameter, redirecting to hash-based route');

      // Build new hash-based URL
      // Priority: hash fragment > query parameter
      // /admin?tab=transfers#settings → /admin#settings (hash wins)
      // /admin?tab=transfers → /admin#transfers (query used if no hash)
      const currentHash = (window.location.hash || '').slice(1); // Remove # if present
      const targetHash = currentHash || tabParam; // Hash takes priority

      // 🛡️ CRITICAL FIX: Use hash update instead of replace to avoid full page reload
      // Only update if different to prevent infinite loops
      const newFullHash = `#${targetHash}`;
      const currentFullHash = window.location.hash || '';

      if (currentFullHash !== newFullHash) {
        // Use hash update (no reload) instead of replace (full reload)
        window.location.hash = targetHash;

        // Clean up query params from URL without reload
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete('tab');
        window.history.replaceState({}, '', newUrl.pathname + newUrl.hash);
      }
    }
  }, [location.search]);

  return <AdminUnifiedLayout />;
};

const GuardedAddressPage = createGuardedRoute("/address", AddressPageVNext, AddressPage);
// Keep guards from createGuardedRoute, but feed the *wrapped* VNext.
// Fallback (LegacyAdminWrapper) remains untouched.
const GuardedAdminPage = createGuardedRoute(
  "/admin",
  AdminDashboardVNextWrapped,
  LegacyAdminWrapper
);
const GuardedAuthPage = createGuardedRoute("/auth", AuthPageVNext, Auth);
const GuardedWalletPage = createGuardedRoute(
  "/app/user/wallet",
  WalletPageVNext,
  () => <div>Legacy Wallet (Not Implemented)</div>
);
const GuardedHistoryPage = createGuardedRoute(
  "/app/user/history",
  HistoryPageVNext,
  () => <div>Legacy History (Not Implemented)</div>
);
const GuardedSettingsPage = createGuardedRoute("/app/user/settings", SettingsPageVNext, UserSettingsPage);
const GuardedAdminBridgePage = createGuardedRoute(
  "/admin/bridge",
  AdminBridgeVNext,
  () => <div>Legacy Admin Bridge (Not Implemented)</div>
);

type KycVariant = 'start' | 'resume' | 'status';

interface KycRouteGuardProps {
  variant: KycVariant;
  fallback: string;
}

function KycRouteGuard({ variant, fallback }: KycRouteGuardProps) {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const bypass = searchParams.has('v');

  if (bypass) {
    return <KycFlowPage mode={variant} />;
  }

  const sanitized = new URLSearchParams(location.search);
  sanitized.delete('v');
  const serialized = sanitized.toString();
  const destination = serialized ? `${fallback}?${serialized}` : fallback;

  return <Navigate to={destination} replace />;
}

export interface RouteTreeConfig {
  readonly isAdminDomain: boolean;
}

export function renderRouteTree({ isAdminDomain }: RouteTreeConfig): React.ReactNode {
  const rootElement = isAdminDomain ? (
    <Navigate to="/admin" replace />
  ) : (
    <OptionalAuth>
      <Landing />
    </OptionalAuth>
  );

  const authRedirectTarget = isAdminDomain ? "/admin" : "/dashboard";

  return (
    <>
      <Route path="/" element={rootElement} />
      <Route
        path="/auth"
        element={
          <RequireGuest>
            <GuardedAuthPage />
          </RequireGuest>
        }
      />
      <Route
        path="/auth/callback"
        element={<UniversalAuthCallback redirectTo={authRedirectTarget} isAdminFlow={isAdminDomain} />}
      />
      {/* 
        ⚠️ DEPRECATED ROUTES (CANON v1.3.0)
        
        /verify/* routes are LEGACY transitional components.
        Canonical user flow is: /auth → /auth/callback → /dashboard (or /onboarding/wizard)
        
        These routes remain for backward compatibility with:
        - Old magic links that may still be in user emails
        - External links from documentation
        - Bookmarks
        
        The Verify component acts as a dispatcher that redirects to canonical destinations.
        
        TODO: Remove these routes in v2.0 after 6-month deprecation period.
        See: KYC_REDIRECT_FLOW_ANALYSIS.md for full architecture documentation.
      */}
      <Route
        path="/verify"
        element={
          <RequireAuth>
            <Verify />
          </RequireAuth>
        }
      />
      <Route
        path="/verify/start"
        element={
          <RequireAuth>
            <Verify variant="start" />
          </RequireAuth>
        }
      />
      <Route
        path="/verify/resume"
        element={
          <RequireAuth>
            <Verify variant="resume" />
          </RequireAuth>
        }
      />
      <Route
        path="/verify/status"
        element={
          <RequireAuth>
            <Verify variant="status" />
          </RequireAuth>
        }
      />
      {/* 
        ⚠️ LEGACY KYC ROUTES (CANON v1.3.0)
        
        /kyc/* routes redirect to /dashboard which is the canonical destination.
        OnboardingWizard (/onboarding/wizard) handles new user KYC flows.
        Dashboard shows verification status via UnifiedVerificationStatusCard.
      */}
      <Route
        path="/kyc/start"
        element={
          <RequireAuth>
            <Navigate to="/dashboard" replace />
          </RequireAuth>
        }
      />
      <Route
        path="/kyc/resume"
        element={
          <RequireAuth>
            <Navigate to="/dashboard" replace />
          </RequireAuth>
        }
      />
      <Route
        path="/kyc/pending"
        element={
          <RequireAuth>
            <Navigate to="/dashboard" replace />
          </RequireAuth>
        }
      />
      <Route
        path="/onboarding/wizard"
        element={
          <RequireAuth>
            <OnboardingWizard />
          </RequireAuth>
        }
      />
      <Route
        path="/onboarding/profile"
        element={
          <RequireAuth>
            <OnboardingProfile />
          </RequireAuth>
        }
      />
      <Route
        path="/onboarding/tos-waiting"
        element={
          <RequireAuth>
            <ToSWaitingPage />
          </RequireAuth>
        }
      />
      <Route
        path="/onboarding/resume"
        element={
          <RequireAuth>
            <ResumeVerificationPage />
          </RequireAuth>
        }
      />
      <Route
        path="/bridge-customer"
        element={
          <RequireAuth>
            <BridgeCustomer />
          </RequireAuth>
        }
      />
      <Route
        path="/dashboard"
        element={
          <RequireAuth>
            <Dashboard />
          </RequireAuth>
        }
      >
        <Route index element={<DashboardIndex />} />
        {/* DEPRECATED: Receive merged into Wallet (Wallet-Centric UX) */}
        <Route path="receive" element={<Navigate to="/dashboard/wallet" replace />} />
        <Route path="send" element={<SendPageRoute />} />
        <Route path="withdraw" element={<WithdrawPage />} />
        <Route path="wallet" element={<WalletOverview />} />
        <Route path="wallet/:id" element={<WalletDetailRoute />} />
        {/* Activity page - user-friendly name for transaction history */}
        <Route path="activity" element={<TransactionHistoryPage />} />
        {/* Redirect /dashboard/transactions to /dashboard/activity (user-friendly) */}
        <Route path="transactions" element={<Navigate to="/dashboard/activity" replace />} />
        <Route path="transactions/:id" element={<TransactionDetailRoute />} />
        {/* Deprecated routes - redirect to canonical pages */}
        <Route path="addresses" element={<Navigate to="/dashboard/wallet" replace />} />
        <Route path="bank-accounts" element={<Navigate to="/dashboard/send" replace />} />
        <Route path="virtual-accounts" element={<Navigate to="/dashboard/wallet" replace />} />
        <Route path="virtual-accounts/:id" element={<VirtualAccountDetailRoute />} />
        {/* Profile route redirects to Settings (consolidated) */}
        <Route path="profile" element={<Navigate to="/dashboard/settings" replace />} />
        <Route path="settings" element={<DashboardSettings />} />
        <Route path="settings/profile" element={<DashboardSettings />} />
        <Route path="settings/notifications" element={<DashboardSettings />} />
        <Route path="help" element={<DashboardHelp />} />
        <Route path="calculator" element={<DashboardCalculator />} />
        <Route path="telegram" element={<DashboardTelegramRoute />} />
      </Route>
      <Route
        path="/add-address"
        element={
          <RequireAuth>
            <AddAddressPage />
          </RequireAuth>
        }
      />
      <Route
        path="/addresses/create"
        element={
          <RequireAuth>
            <CreateAddressPage />
          </RequireAuth>
        }
      />
      <Route
        path="/add-bank-account"
        element={
          <RequireAuth>
            <Navigate to="/dashboard/send?action=add-bank-account" replace />
          </RequireAuth>
        }
      />
      <Route
        path="/withdraw"
        element={
          <RequireAuth>
            <Navigate to="/dashboard/withdraw" replace />
          </RequireAuth>
        }
      />
      <Route
        path="/address"
        element={
          <RequireAuth>
            <GuardedAddressPage />
          </RequireAuth>
        }
      />
      <Route
        path="/app/user/address"
        element={
          <RequireAuth>
            <GuardedAddressPage />
          </RequireAuth>
        }
      />
      <Route path="/how-it-works" element={<HowItWorks />} />
      <Route path="/coverage" element={<Coverage />} />
      <Route path="/for-freelancers" element={<ForFreelancers />} />
      <Route path="/for-business" element={<ForBusiness />} />
      <Route path="/for-global-earners" element={<ForGlobalEarners />} />
      <Route path="/for-finance-teams" element={<ForFinanceTeams />} />
      <Route path="/for-crypto-businesses" element={<ForCryptoBusinesses />} />
      <Route path="/how-payments-work" element={<HowPaymentsWork />} />
      <Route path="/support" element={<SupportCenter />} />
      <Route
        path="/support/tickets"
        element={
          <RequireAuth>
            <SupportTickets />
          </RequireAuth>
        }
      />
      <Route
        path="/support/tickets/:id"
        element={
          <RequireAuth>
            <SupportTicketDetail />
          </RequireAuth>
        }
      />
      <Route path="/products" element={<ProductsOverview />} />
      <Route path="/products/liquidation-address" element={<LiquidationAddressPage />} />
      <Route path="/products/virtual-account" element={<VirtualAccountPage />} />
      <Route path="/products/wallet" element={<WalletPage />} />
      <Route path="/products/payout-routes" element={<PayoutRoutesPage />} />
      <Route
        path="/waitlist"
        element={
          <OptionalAuth>
            <WaitlistPage />
          </OptionalAuth>
        }
      />
      <Route
        path="/waitlist/confirm"
        element={
          <OptionalAuth>
            <WaitlistConfirmPage />
          </OptionalAuth>
        }
      />
      <Route
        path="/fee"
        element={
          <OptionalAuth>
            <FeePage />
          </OptionalAuth>
        }
      />
      {/* Redirect /pricing to /fee for backward compatibility */}
      <Route
        path="/pricing"
        element={<Navigate to="/fee" replace />}
      />
      <Route
        path="/enterprise"
        element={
          <OptionalAuth>
            <EnterprisePage />
          </OptionalAuth>
        }
      />
      <Route
        path="/design/ui-kit"
        element={<UiKitShowcase />}
      />
      <Route
        path="/design/input-demo"
        element={<InputDemo />}
      />
      <Route
        path="/design/canon"
        element={<CanonPlayground />}
      />
      <Route
        path="/brand"
        element={
          <OptionalAuth>
            <BrandAssetsPage />
          </OptionalAuth>
        }
      />
      <Route
        path="/brand/platforms"
        element={
          <OptionalAuth>
            <BrandPlatformsPage />
          </OptionalAuth>
        }
      />
      <Route
        path="/analytics"
        element={
          <RequireAuth>
            <Analytics />
          </RequireAuth>
        }
      />
      <Route
        path="/app/user/wallet"
        element={
          <RequireAuth>
            <WalletPageRoute />
          </RequireAuth>
        }
      />
      <Route
        path="/app/user/history"
        element={
          <RequireAuth>
            <GuardedHistoryPage />
          </RequireAuth>
        }
      />
      <Route
        path="/app/user/settings"
        element={
          <RequireAuth>
            <GuardedSettingsPage />
          </RequireAuth>
        }
      />
      <Route path="/settings" element={<Navigate to="/app/user/settings" replace />} />
      <Route
        path="/checkout/new"
        element={
          <RequireAuth>
            <CheckoutNew />
          </RequireAuth>
        }
      />
      <Route
        path="/checkout/:id"
        element={
          <RequireAuth>
            <CheckoutShow />
          </RequireAuth>
        }
      />
      {/* 
        Telegram Mini App Routes
        
        Architecture: Single UI approach
        - /tg → TgAuth (Telegram initData → Supabase session)
        - After auth → redirect to /dashboard (main responsive UI)
        - Removed duplicate /tg/dashboard, /tg/kyc, /tg/deposit, /tg/send
        - Main dashboard has Telegram-aware features (haptic, no signout)
      */}
      <Route path="/tg" element={<TelegramShell />}>
        <Route index element={<TgAuth />} />
        {/* Legacy routes redirect to main dashboard */}
        <Route path="dashboard" element={<Navigate to="/dashboard" replace />} />
        <Route path="kyc" element={<Navigate to="/dashboard" replace />} />
        <Route path="deposit" element={<Navigate to="/dashboard/wallet" replace />} />
        <Route path="send" element={<Navigate to="/dashboard/send" replace />} />
      </Route>
      <Route path="/telegram" element={<Navigate to="/tg" replace />} />
      <Route path="/telegram-dashboard" element={<Navigate to="/dashboard" replace />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/p3-bridge-demo" element={<P3BridgeDemo />} />
      <Route path="/user-wallet-demo" element={<UserWalletDemo />} />
      <Route path="/hero-3d-demo" element={<Hero3DDemo />} />
      <Route path="/design/supafab-showcase" element={<SupaFABShowcase />} />
      <Route
        path="/wallet"
        element={
          <RequireAuth>
            <WalletPageRoute />
          </RequireAuth>
        }
      />
      <Route path="/admin-access-denied" element={<AdminAccessDenied />} />
      <Route path="/user-access-denied" element={<UserAccessDenied />} />
      <Route
        path="/admin-access-check"
        element={
          <RequireAuth>
            <AdminAccessCheck />
          </RequireAuth>
        }
      />
      <Route
        path="/admin"
        element={
          <RouteGuard mode="admin">
            <GuardedAdminPage />
          </RouteGuard>
        }
      />
      <Route
        path="/app/admin/dashboard"
        element={
          <RouteGuard mode="admin">
            <GuardedAdminPage />
          </RouteGuard>
        }
      />
      <Route
        path="/admin/bridge"
        element={
          <RouteGuard mode="admin">
            <GuardedAdminBridgePage />
          </RouteGuard>
        }
      />
      <Route path="/admin/waitlist" element={<Navigate to="/admin#ops" replace />} />
      <Route path="/admin/wallet" element={<Navigate to="/admin#ops" replace />} />
      <Route path="/admin/ops" element={<Navigate to="/admin#ops" replace />} />
      <Route path="/admin/email" element={<Navigate to="/admin#settings?tab=email" replace />} />
      <Route path="/admin/security" element={<Navigate to="/admin#settings?tab=security" replace />} />
      <Route path="/admin/system" element={<Navigate to="/admin#settings?tab=system" replace />} />
      <Route path="/admin/activity" element={<Navigate to="/admin#activity" replace />} />
      <Route path="/admin/bi" element={<Navigate to="/admin#overview" replace />} />
      <Route path="/admin/customers" element={<Navigate to="/admin#customers" replace />} />
      <Route path="/admin/qa" element={<Navigate to="/admin#qa" replace />} />
      {["/admin-new", "/admin-old", "/admin-legacy", "/admin-v1", "/admin2", "/admin_panel", "/adm", "/backoffice", "/admin-gate-debug"].map(
        (path) => (
          <Route key={path} path={path} element={<Navigate to="/admin" replace />} />
        )
      )}
      <Route path="/admin-/*" element={<Navigate to="/admin" replace />} />
      <Route
        path="/admin/gate-debug"
        element={
          <RouteGuard mode="admin">
            <AdminGateDebug />
          </RouteGuard>
        }
      />
      <Route path="*" element={<NotFound />} />
    </>
  );
}
